import api from './api';
import { UserPermission, GrantAccessPayload, AccessModule, AccessPermission, PermissionItem, GrantedToItem, PatchGrantedToItem } from '../types/access-control.types';

/**
 * Get the list of permissions granted by the current user to others.
 */
export const getGrantedPermissions = async (): Promise<UserPermission[]> => {
  const response = await api.get<{ data: UserPermission[] }>('/access-control/granted-permissions');
  return response.data.data;
};

/**
 * Get the list of permissions granted to the current user by others.
 */
export const getMyPermissions = async (): Promise<UserPermission[]> => {
  const response = await api.get<{ data: UserPermission[] }>('/access-control/my-permissions');
  return response.data.data;
};

/**
 * Get all permissions for a specific user.
 * @param userId - The ID of the user.
 */
export const getPermissionsForUser = async (userId: number): Promise<UserPermission[]> => {
  const response = await api.get<{ data: UserPermission[] }>(`/access-control/permissions/${userId}`);
  return response.data.data;
};


/**
 * Grant a new access permission to a user.
 * @param payload - The details of the access to be granted.
 */
export const grantAccess = async (payload: GrantAccessPayload): Promise<UserPermission> => {
  const response = await api.post<{ data: UserPermission }>('/access-control/grant', payload);
  return response.data.data;
};

/**
 * Revoke an existing access permission.
 * @param permissionId - The ID of the permission to revoke.
 */
export const revokeAccess = async (permissionId: number): Promise<void> => {
  await api.delete(`/access-control/revoke/${permissionId}`);
};

/**
 * Get the default permissions structure for building the permissions UI.
 */
export const getDefaultPermissions = async (): Promise<PermissionItem[]> => {
  const response = await api.get<{ data: PermissionItem[] }>('/access-control/permissions');
  return response.data.data;
};

/**
 * Get permissions the current client has granted to a specific expert.
 * @param expertId - The ID of the expert.
 */
export const getGrantedToExpert = async (expertId: number): Promise<GrantedToItem[]> => {
  const response = await api.get<{ data: GrantedToItem[] }>(`/access-control/granted-to/${expertId}`);
  return response.data.data;
};

/**
 * Replace all permissions granted to a specific expert.
 * @param expertId - The ID of the expert.
 * @param permissions - The new permissions array (pass [] to revoke all).
 */
export const updateGrantedToExpert = async (expertId: number, items: PatchGrantedToItem[]): Promise<void> => {
  await api.patch(`/access-control/granted-to/${expertId}`, items);
};

/**
 * Check if a user has a specific permission.
 * @param granteeId - The ID of the user whose access is being checked.
 * @param granterId - The ID of the user who is supposed to have granted access.
 * @param moduleCode - The module to check access for.
 * @param permission - The permission to check for (read/write).
 */
export const checkAccess = async (granteeId: number, granterId: number, moduleCode: AccessModule, permission: AccessPermission): Promise<boolean> => {
  const response = await api.get<{ data: { hasAccess: boolean } }>('/access-control/check', {
    params: {
      granteeId,
      granterId,
      moduleCode,
      permission,
    },
  });
  return response.data.data.hasAccess;
};
